import { useState, useEffect } from 'react';
import { Save, Smartphone, Building2, Loader2 } from 'lucide-react';
import { apiCall } from './api';
import { toast } from 'sonner';

/**
 * Admin : configurer les coordonnées de paiement (Mobile Money + virement bancaire).
 */
export default function AdminPaymentSettings({ token }) {
  const [s, setS] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    apiCall('/admin/payment-settings', 'GET', null, token).then(r => { setS(r); setLoading(false); }).catch(() => setLoading(false));
  }, [token]);

  const upd = (k, v) => setS(prev => ({ ...prev, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await apiCall('/admin/payment-settings', 'PUT', s, token);
      toast.success('Coordonnées sauvegardées');
    } catch (e) { toast.error(e.message); }
    setSaving(false);
  };

  if (loading) return <div className="text-center py-10"><Loader2 className="animate-spin mx-auto" /></div>;

  const Field = ({ k, label, placeholder, testid }) => (
    <div>
      <label className="text-xs text-white/60 block mb-1">{label}</label>
      <input value={s[k] ?? ''} onChange={e => upd(k, e.target.value)} placeholder={placeholder} data-testid={testid}
        className="w-full bg-slate-900 border border-white/10 rounded-lg p-2 text-sm" />
    </div>
  );

  return (
    <div data-testid="admin-payment-settings">
      <h3 className="font-bold text-lg mb-2">Coordonnées de paiement</h3>
      <p className="text-sm text-white/60 mb-5">Tes numéros sont affichés aux étudiants quand ils achètent un module premium. Laisse vide pour cacher un mode de paiement.</p>

      <div className="bg-white/5 border border-white/10 rounded-2xl p-5 space-y-5">
        <div>
          <h4 className="font-bold mb-3 flex items-center gap-2"><Smartphone size={16} className="text-emerald-300" />Mobile Money</h4>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field k="mpesa_number" label="M-PESA — Numéro" placeholder="+243 ..." testid="set-mpesa-number" />
            <Field k="mpesa_name" label="M-PESA — Nom du titulaire" placeholder="Sandra ..." testid="set-mpesa-name" />
            <Field k="orange_number" label="Orange Money — Numéro" placeholder="+243 ..." testid="set-orange-number" />
            <Field k="orange_name" label="Orange Money — Nom" placeholder="" testid="set-orange-name" />
            <Field k="mtn_number" label="MTN Money — Numéro" placeholder="+243 ..." testid="set-mtn-number" />
            <Field k="mtn_name" label="MTN Money — Nom" placeholder="" testid="set-mtn-name" />
          </div>
        </div>

        <div>
          <h4 className="font-bold mb-3 flex items-center gap-2"><Building2 size={16} className="text-emerald-300" />Virement bancaire</h4>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field k="bank_name" label="Nom de la banque" placeholder="Equity BCDC ..." testid="set-bank-name" />
            <Field k="bank_account" label="Numéro de compte / IBAN" placeholder="" testid="set-bank-account" />
            <Field k="bank_holder" label="Titulaire" placeholder="JoSmartTech / ..." testid="set-bank-holder" />
            <Field k="bank_swift" label="SWIFT / BIC" placeholder="(international)" testid="set-bank-swift" />
          </div>
        </div>

        <div>
          <h4 className="font-bold mb-3 flex items-center gap-2"><Building2 size={16} className="text-emerald-300" />e-Transfert (Interac)</h4>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field k="etransfer_email" label="Adresse e-Transfert (email)" placeholder="support.josmarttech@gmail.com" testid="set-etransfer-email" />
          </div>
          <div className="mt-3">
            <label className="text-xs text-white/60 block mb-1">Instructions e-Transfert</label>
            <textarea value={s.etransfer_instructions ?? ''} onChange={e => upd('etransfer_instructions', e.target.value)} rows={2}
              placeholder="Ex: Envoyez le virement Interac puis la capture à cette adresse. Validation 1-30 min."
              className="w-full bg-slate-900 border border-white/10 rounded-lg p-2 text-sm" data-testid="set-etransfer-instructions" />
          </div>
        </div>

        <div>
          <label className="text-xs text-white/60 block mb-1">Instructions affichées à l'étudiant (optionnel)</label>
          <textarea value={s.instructions ?? ''} onChange={e => upd('instructions', e.target.value)} rows={3}
            placeholder="Ex: Mentionner ton email dans le motif du paiement"
            className="w-full bg-slate-900 border border-white/10 rounded-lg p-2 text-sm" data-testid="set-instructions" />
        </div>

        <button onClick={save} disabled={saving} data-testid="save-payment-settings"
          className="bg-emerald-500 hover:bg-emerald-400 disabled:opacity-40 text-slate-950 font-bold py-2 px-5 rounded-lg flex items-center gap-2">
          {saving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />}Sauvegarder
        </button>
      </div>
    </div>
  );
}
